/* Shreya — announcement bar: rotating messages + dismiss */
(() => {
  if (customElements.get('announcement-bar')) return;

  const STORAGE_PREFIX = 'shreya:announcement:';
  const storage = {
    get(key) { try { return window.localStorage.getItem(key); } catch (_) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch (_) { /* storage unavailable */ } },
  };

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.dismissKey = this.dataset.dismissKey;
      if (this.dismissKey && !window.Shopify?.designMode && storage.get(STORAGE_PREFIX + this.dismissKey) === '1') {
        this.classList.add('is-dismissed');
        return;
      }

      const close = this.querySelector('[data-announcement-close]');
      if (close) close.addEventListener('click', () => this.dismiss());

      this.viewport = this.querySelector('[data-announcement-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      if (this.dataset.mode !== 'slider' || this.slides.length < 2) return;

      this.current = 0;
      this.paused = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.step(-1, true));
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.step(1, true));

      // Pause while the visitor is reading / interacting
      this.addEventListener('mouseenter', () => { this.paused = true; });
      this.addEventListener('mouseleave', () => { this.paused = false; });
      this.addEventListener('focusin', () => { this.paused = true; });
      this.addEventListener('focusout', () => { this.paused = false; });

      // Swipe on touch devices
      let startX = null;
      this.viewport.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.viewport.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1, true);
        startX = null;
      });

      this.onBlockSelect = (e) => {
        const idx = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (idx > -1) { this.paused = true; this.show(idx, idx > this.current ? 1 : -1); }
      };
      this.onBlockDeselect = () => { this.paused = false; };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.start();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    start() {
      const seconds = parseFloat(this.dataset.autoplay || '0');
      if (!seconds || this.reduceMotion) return;
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        if (!this.paused && !document.hidden) this.step(1);
      }, seconds * 1000);
    }

    step(dir, manual) {
      // Only announce changes the visitor asked for, not the automatic rotation
      this.viewport.setAttribute('aria-live', manual ? 'polite' : 'off');
      const next = (this.current + dir + this.slides.length) % this.slides.length;
      this.show(next, dir);
      if (manual) this.start(); // restart the timer after manual navigation
    }

    show(index, dir = 1) {
      if (index === this.current) return;
      const prev = this.slides[this.current];
      const next = this.slides[index];
      this.viewport.dataset.direction = dir < 0 ? 'prev' : 'next';
      this.slides.forEach((s) => s.classList.remove('is-leaving'));
      prev.classList.remove('is-active');
      prev.classList.add('is-leaving');
      next.classList.add('is-active');
      [prev, next].forEach((slide) => {
        const active = slide === next;
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a, button').forEach((el) => { el.tabIndex = active ? 0 : -1; });
      });
      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => prev.classList.remove('is-leaving'), 650);
      this.current = index;
    }

    dismiss() {
      if (this.dismissKey) storage.set(STORAGE_PREFIX + this.dismissKey, '1');
      this.classList.add('is-dismissed');
      window.dispatchEvent(new Event('resize'));
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
})();
